import { UseMutationOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useToast } from "native-base";

import Toast from "@/components/atoms/Toast";
import { useAppDispatch, useAppSelector } from "@/hooks/application/store";
import { ApiError, ApiValidationError } from "@/interfaces/common/Error";
import { Address } from "@/interfaces/domain/Address";
import {
  CreateAddressParams,
  createAddressRequest,
  deleteAddressRequest,
} from "@/services/api/address";
import { updateClientRequest } from "@/services/api/client";
import { logger } from "@/services/logger";
import { UPDATE_CLIENT } from "@/store/ducks/auth";
import { SET_ADDRESS } from "@/store/ducks/checkout";

export const useAddress = () => {
  const { user, logged } = useAppSelector((store) => store.auth);
  const address = useAppSelector((store) => store.checkout.address);

  const dispatch = useAppDispatch();

  const queryClient = useQueryClient();
  const toast = useToast();

  const setDeliveryAddress = (address: Address) => {
    dispatch(SET_ADDRESS(address));
  };

  const changePrincipalAddress = async (address: Address) => {
    try {
      const client = await updateClientRequest(user?.cliente?._id, {
        endereco_principal: address._id,
      });
      setDeliveryAddress(address);
      dispatch(UPDATE_CLIENT(client));
      queryClient.invalidateQueries();
    } catch (error) {
      toast.show({
        render: () => <Toast variant="error" message={error.response.data.error} />,
      });
    }
  };

  return {
    addressList: logged ? user?.cliente?.enderecos : [],
    principalAddress: logged ? address : null,
    changePrincipalAddress,
  };
};

export const useCreateAddress = (
  options?: UseMutationOptions<
    Address,
    AxiosError<ApiError & ApiValidationError>,
    CreateAddressParams
  >
) => {
  const { user } = useAppSelector((store) => store.auth);
  const dispatch = useAppDispatch();
  const toast = useToast();

  return useMutation<Address, AxiosError<ApiError & ApiValidationError>, CreateAddressParams>({
    mutationFn: createAddressRequest,
    onSuccess: async (data, variables, context) => {
      try {
        let addressList = user?.cliente?.enderecos;
        if (addressList?.length) {
          addressList.push(data);
        } else {
          addressList = [data];
        }
        const client = await updateClientRequest(user?.cliente?._id, {
          endereco_principal: data._id,
          enderecos: addressList.map((a) => a._id),
        });
        dispatch(SET_ADDRESS(data));
        dispatch(UPDATE_CLIENT(client));
      } catch (error) {
        logger.error(error);
        if (typeof options?.onError === "function") {
          options?.onError(error, variables, context);
        }
        toast.show({
          render: () => <Toast variant="error" message={error.response.data.error} />,
        });
      }
      toast.show({
        render: () => <Toast variant="success" message="Endereço cadastrado!" />,
      });
      if (options?.onSuccess) {
        options?.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      logger.error(error);
      toast.show({
        render: () => <Toast variant="error" message={error.response.data.error} />,
      });
      options?.onError(error, variables, context);
    },
  });
};

export const useDeleteAddress = (
  options?: UseMutationOptions<void, AxiosError<ApiError & ApiValidationError>, string>
) => {
  const { user } = useAppSelector((store) => store.auth);
  const dispatch = useAppDispatch();
  const toast = useToast();

  return useMutation<void, AxiosError<ApiError & ApiValidationError>, string>(
    deleteAddressRequest,
    {
      onSuccess: async (data, id, context) => {
        try {
          let addressList = user?.cliente?.enderecos;
          let principalAddressId = user?.cliente?.endereco_principal?._id;
          if (addressList?.length) {
            addressList = addressList.filter((addr) => addr._id !== id);
          }
          if (principalAddressId === id) {
            principalAddressId = null;
          }
          const client = await updateClientRequest(user?.cliente?._id, {
            endereco_principal: principalAddressId,
            enderecos: addressList.map((a) => a._id),
          });
          if (!principalAddressId) {
            dispatch(SET_ADDRESS(null));
          }
          dispatch(UPDATE_CLIENT(client));
        } catch (error) {
          if (typeof options?.onError === "function") {
            options?.onError(error, id, context);
          }
          toast.show({
            render: () => <Toast variant="error" message={error.response.data.error} />,
          });
        }
        toast.show({
          render: () => <Toast variant="success" message="Endereço deletado!" />,
        });
        options?.onSuccess(data, id, context);
      },
      onError: (error, variables, context) => {
        logger.error(error);
        toast.show({
          render: () => <Toast variant="error" message={error.response.data.error} />,
        });
        options?.onError(error, variables, context);
      },
    }
  );
};
